import React, { useEffect, useState } from 'react';
import type { User, PathwayBlock, BlockTrainingRoomLink } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { BookOpen, ExternalLink, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';

interface TheoryBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
}

export const TheoryBlockView: React.FC<TheoryBlockViewProps> = ({ user, block, pathwayId }) => {
  const { t } = useTranslation();
  const [link, setLink] = useState<BlockTrainingRoomLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await DataService.getBlockTrainingRoomLink(user.id, block.id, pathwayId);
        if (!data) {
          setLink(null);
          return;
        }

        let currentLink = data;

        // Auto-detect completion by checking Trainingroom courses
        const templateId = block.courseTemplateId || data.templateId;
        if (templateId && data.status !== 'complete') {
          const courses = await DataService.getTrainingRoomCourses(user.id, templateId);
          let matchingCourse = data.courseId ? courses.find(c => c.id === data.courseId) : undefined;
          if (!matchingCourse) {
            matchingCourse = courses.find(c => DataService.checkCourseValidity(c).isValid) || undefined;
          }
          if (matchingCourse) {
            const validity = DataService.checkCourseValidity(matchingCourse);
            const newStatus = validity.isComplete
              ? (validity.isValid ? 'complete' : 'expired')
              : 'in_progress';
            const courseIdChanged = matchingCourse.id !== data.courseId;
            if (data.status !== newStatus || courseIdChanged) {
              let completionDate: number | undefined;
              if (validity.isComplete && matchingCourse.signeddate) {
                completionDate = matchingCourse.signeddate.toDate
                  ? matchingCourse.signeddate.toDate().getTime()
                  : new Date(matchingCourse.signeddate).getTime();
              }
              const updates: Partial<BlockTrainingRoomLink> = { status: newStatus };
              if (completionDate) updates.completionDate = completionDate;
              if (courseIdChanged) updates.courseId = matchingCourse.id;
              await DataService.updateBlockTrainingRoomLink(data.id, updates);
              currentLink = { ...data, ...updates };
            }
          }
        }

        setLink(currentLink);

        // Write block_progress when complete so PathwayView can unlock next block
        if (currentLink.status === 'complete') {
          await DataService.setBlockProgress({
            candidateId: user.id,
            pathwayId,
            blockId: block.id,
            status: 'complete',
            completedAt: currentLink.completionDate,
          });
        }
      } catch (e) {
        console.error('Failed to load training room link', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const statusIcons: Record<string, React.FC<any>> = {
    not_started: Clock,
    in_progress: Clock,
    complete: CheckCircle,
    expired: XCircle,
  };

  const statusColors: Record<string, string> = {
    not_started: 'text-aero-500',
    in_progress: 'text-blue-500',
    complete: 'text-green-500',
    expired: 'text-red-500',
  };

  const statusBgs: Record<string, string> = {
    not_started: 'bg-aero-50 border-aero-200',
    in_progress: 'bg-blue-50 border-blue-200',
    complete: 'bg-green-50 border-green-200',
    expired: 'bg-red-50 border-red-200',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-aero-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.theory.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>
      </div>

      {/* Course card */}
      {!link ? (
        <div className="bg-aero-50 rounded-xl border border-aero-200 p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-aero-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-aero-600">{t('candidate.block.theory.notAssigned')}</p>
        </div>
      ) : (
        <div className={`rounded-xl border-2 p-5 ${statusBgs[link.status] || statusBgs.not_started}`}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">Course</p>
              <h3 className="text-base font-semibold text-aero-900">{link.metadata?.courseTitle || block.courseTemplateName || block.name}</h3>
            </div>
            {(() => {
              const StatusIcon = statusIcons[link.status] || Clock;
              return (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <StatusIcon className={`w-4 h-4 ${statusColors[link.status] || statusColors.not_started}`} />
                  <span className={`text-xs font-semibold ${statusColors[link.status] || statusColors.not_started}`}>
                    {t(`candidate.block.theory.status.${link.status}`)}
                  </span>
                </div>
              );
            })()}
          </div>

          {link.accessCode && (
            <div className="mb-4 bg-white/70 rounded-lg px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-aero-500">{t('candidate.block.theory.accessCode')}</span>
              <span className="text-sm font-mono font-bold text-aero-900">{link.accessCode}</span>
            </div>
          )}

          {link.status !== 'complete' && link.accessCode && (
            <a
              href={`https://t-r.app/${link.accessCode}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-aero-600 hover:bg-aero-700 text-white rounded-xl font-semibold text-sm transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              {t('candidate.block.theory.openCourse')}
            </a>
          )}

          {link.status === 'complete' && (
            <div className="flex items-center justify-center gap-2 py-3 bg-green-100 text-green-700 rounded-xl font-semibold text-sm">
              <CheckCircle className="w-4 h-4" />
              {t('common.complete')}
              {link.completionDate && (
                <span className="font-normal text-green-600 ml-1">· {new Date(link.completionDate).toLocaleDateString()}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
